import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormDialog } from '@/components/ui/form-dialog';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { countOf } from '@/lib/plural';

/** POS-I373: CSV import for categories and brands: template, file, preview (dry run), confirm. */
interface ImportRow { line: number; name: string; action: 'create' | 'skip' | 'error'; reason?: string }
interface ImportResult {
  dryRun: boolean;
  rows: ImportRow[];
  summary: { create: number; skip: number; error: number; created: number };
}

const MAX_BYTES = 90_000;
const MAX_ROWS = 1000;

export async function downloadCsv(path: string, fileName: string) {
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

const ACTION_LABEL = { create: 'Will be added', skip: 'Already exists, skipped', error: 'Not imported' } as const;
const ACTION_TONE = { create: 'success', skip: 'outline', error: 'danger' } as const;

export function ImportCsvDialog({
  open, onClose, noun, nounOne, basePath, onImported,
}: { open: boolean; onClose: () => void; noun: string; nounOne: string; basePath: string; onImported: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  function reset() {
    setCsv(''); setFileName(''); setFileError(''); setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  }
  function close() { reset(); onClose(); }

  const run = useMutation({
    mutationFn: async (v: { text: string; dryRun: boolean }) => (await api.post<ImportResult>(basePath + '/import', { csv: v.text, dryRun: v.dryRun })).data,
    onSuccess: (data) => {
      setResult(data);
      if (!data.dryRun) {
        onImported();
        // POS-I378: one summary toast (a fixed id keeps a repeated call from stacking a second one).
        const { created, skip, error } = data.summary;
        toast.success(countOf(created, 'row', 'rows') + ' imported' + (skip > 0 ? ', ' + skip + ' skipped' : '') + (error > 0 ? ', ' + error + ' not imported' : ''), { id: 'csv-import-done' });
      }
    },
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'The file could not be checked'), { id: 'csv-import-error' }),
  });

  async function onFile(file: File | undefined) {
    setResult(null); setFileError('');
    if (!file) { setCsv(''); setFileName(''); return; }
    if (!/\.csv$/i.test(file.name)) { setFileError('Choose a .csv file. Download the template to see the columns.'); setCsv(''); return; }
    if (file.size > MAX_BYTES) { setFileError('This file is too large. Keep it under 90 KB and ' + MAX_ROWS + ' rows, or split it.'); setCsv(''); return; }
    const text = await file.text();
    setFileName(file.name);
    setCsv(text);
    run.mutate({ text, dryRun: true });
  }

  const done = !!result && !result.dryRun;
  const canConfirm = !!result && result.dryRun && result.summary.create > 0;

  return (
    <FormDialog
      open={open}
      onClose={close}
      title={'Import ' + noun}
      subtitle="Upload a CSV file. Nothing is saved until you confirm the preview."
      width="46rem"
      submitLabel={done ? 'Done' : 'Import ' + countOf(result?.summary.create ?? 0, nounOne, noun)}
      isPending={run.isPending}
      submitDisabled={done ? false : !canConfirm}
      onSubmit={(e) => { e.preventDefault(); if (done) close(); else if (canConfirm) run.mutate({ text: csv, dryRun: false }); }}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={() => downloadCsv(basePath + '/import-template.csv', noun.toLowerCase() + '_template.csv').catch(() => toast.error('Download failed'))}>
            <Download className="me-2 h-4 w-4" />
            Download template
          </Button>
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={run.isPending || done}>
            <Upload className="me-2 h-4 w-4" />
            {fileName ? 'Choose another file' : 'Choose CSV file'}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            aria-label="CSV file"
            onChange={(e) => { void onFile(e.target.files?.[0]); }}
          />
          {fileName && <span className="text-sm text-muted-foreground">{fileName}</span>}
        </div>
        <p className="text-xs text-muted-foreground">Up to {MAX_ROWS} rows and 90 KB. Names that already exist are skipped, never changed.</p>
        {fileError && <p role="alert" className="text-sm text-destructive-text">{fileError}</p>}
        {run.isError && !result && <p role="alert" className="text-sm text-destructive-text">{getApiErrorMessage(run.error, 'The file could not be checked')}</p>}

        {result && (
          <>
            <p className="text-sm font-medium" role="status">
              {done
                ? result.summary.created + ' imported, ' + result.summary.skip + ' skipped, ' + result.summary.error + ' not imported'
                : result.summary.create + ' to add, ' + result.summary.skip + ' already exist, ' + result.summary.error + ' with errors'}
            </p>
            <div className="max-h-72 overflow-auto rounded-md border border-border">
              <Table>
                <THead>
                  <Tr hover={false}>
                    <Th>Line</Th>
                    <Th>Name</Th>
                    <Th>Result</Th>
                    <Th>Reason</Th>
                  </Tr>
                </THead>
                <TBody>
                  {result.rows.map((r) => (
                    <Tr key={r.line}>
                      <Td>{r.line}</Td>
                      <Td className="max-w-[12rem] truncate" title={r.name}>{r.name || '—'}</Td>
                      <Td><StatusBadge status={r.action} tone={ACTION_TONE[r.action]} label={done && r.action === 'create' ? 'Added' : ACTION_LABEL[r.action]} /></Td>
                      <Td className="text-muted-foreground">{r.reason ?? ''}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </FormDialog>
  );
}
